/* ============================================================================
   domain_selector — field-spec model + a STATIC spec for Slice 1.

   A FieldSpec describes a model's fields (name/label/type/relation/selection)
   so the builder can offer type-driven operators and drill relations into
   field chains. Slice 1 ships a static spec; Slice 3 replaces `resolveSpec`
   with a live model-field introspection read over the ir.model.field mirror.
   ========================================================================== */
import { RELATIONAL_TYPES } from "./operators";

export interface FieldSpec {
    name: string;
    label: string;
    type: string;
    /** target model key for relational fields (m2o/m2m/o2m). */
    rel?: string;
    /** options for enum/selection fields. */
    sel?: string[];
    /** dynamic-value token offered for this field (e.g. "$principal.user_id"). */
    dyn?: string;
}

export interface ModelSpec {
    label: string;
    fields: FieldSpec[];
}

export type SpecGraph = Record<string, ModelSpec>;

/** Sample related records for value pickers (Slice 3 → `/related/<field>`). */
export const STATIC_M2O: Record<string, Array<[string, string]>> = {
    "res.partner": [["p-acme", "Acme Corp"], ["p-globex", "Globex"], ["p-wayne", "Wayne Ent"], ["p-stark", "Stark Inds"]],
    "res.user": [["u-pmehta", "Priya Mehta"], ["u-vrao", "Vikram Rao"], ["u-rkapoor", "Rahul Kapoor"], ["u-dsoni", "Administrator"]],
    "res.team": [["TEAM_MUM_SALES", "Mumbai Sales"], ["TEAM_PRICING", "Pricing Desk"], ["TEAM_MUM_OPS", "Mumbai Operations"]],
    "res.country": [["IN", "India"], ["SG", "Singapore"], ["AE", "UAE"], ["US", "United States"]],
    "res.tag": [["t-hot", "Hot"], ["t-cold", "Cold"], ["t-vip", "VIP"]],
};

export const DYN_LABEL: Record<string, string> = {
    "$principal.user_id": "the current user",
    "$principal.team_ids": "the user's teams",
    "$principal.org_ids": "the user's organizations",
    "$today": "today",
};

/** Static spec graph used by Slice 1 (mirrors the frozen mockup). */
export const STATIC_SPEC: SpecGraph = {
    "crm.lead": { label: "Lead", fields: [
        { name: "name", label: "Name", type: "char" },
        { name: "customer_id", label: "Customer", type: "m2o", rel: "res.partner" },
        { name: "stage", label: "Stage", type: "enum", sel: ["New", "Qualified", "Proposal", "Won", "Lost"] },
        { name: "owner_id", label: "Owner", type: "m2o", rel: "res.user", dyn: "$principal.user_id" },
        { name: "team_id", label: "Team", type: "m2o", rel: "res.team", dyn: "$principal.team_ids" },
        { name: "tag_ids", label: "Tags", type: "m2m", rel: "res.tag" },
        { name: "est_value", label: "Estimated value", type: "number" },
        { name: "lead_date", label: "Lead date", type: "date" },
        { name: "created_at", label: "Created on", type: "datetime" },
        { name: "active", label: "Active", type: "bool" },
    ] },
    "res.partner": { label: "Contact", fields: [
        { name: "name", label: "Name", type: "char" },
        { name: "is_company", label: "Is a company", type: "bool" },
        { name: "country_id", label: "Country", type: "m2o", rel: "res.country" },
    ] },
    "res.user": { label: "User", fields: [
        { name: "name", label: "Name", type: "char" },
        { name: "email", label: "Email", type: "char" },
        { name: "team_id", label: "Team", type: "m2o", rel: "res.team" },
    ] },
    "res.team": { label: "Team", fields: [
        { name: "name", label: "Name", type: "char" },
        { name: "organization_id", label: "Organization", type: "m2o", rel: "res.country" },
    ] },
    "res.country": { label: "Country", fields: [{ name: "name", label: "Name", type: "char" }, { name: "code", label: "Code", type: "char" }] },
    "res.tag": { label: "Tag", fields: [{ name: "name", label: "Name", type: "char" }] },
};

export const isRelationalType = (t: string): boolean => RELATIONAL_TYPES.has(t);

export interface ChainMeta {
    field: FieldSpec | null;
    type: string;
    rel?: string;
    sel?: string[];
    dyn?: string;
    label: string;
}

/** Walk a dotted field chain against the spec, returning the terminal meta. */
export function walkChain(spec: SpecGraph, model: string, chain: string): ChainMeta {
    const segs = chain.split(".");
    let m = model;
    let fm: FieldSpec | null = null;
    const labels: string[] = [];
    for (let i = 0; i < segs.length; i++) {
        const model_ = spec[m];
        fm = model_ ? model_.fields.find((f) => f.name === segs[i]) ?? null : null;
        if (!fm) return { field: null, type: "char", label: chain };
        labels.push(fm.label);
        if (i < segs.length - 1) {
            if (!fm.rel || !spec[fm.rel]) break;
            m = fm.rel;
        }
    }
    return { field: fm, type: fm!.type, rel: fm!.rel, sel: fm!.sel, dyn: fm!.dyn, label: labels.join(" › ") };
}

/** Model reached after following a drill path of relational segments. */
export function modelAtDrill(spec: SpecGraph, model: string, drill: string[]): string {
    let m = model;
    for (const seg of drill) {
        const f = spec[m]?.fields.find((x) => x.name === seg);
        if (!f?.rel) break;
        m = f.rel;
    }
    return m;
}

export const m2oLabel = (rel: string, id: string): string =>
    (STATIC_M2O[rel]?.find((r) => r[0] === id) ?? [id, id])[1];
